import Enumerable from "linq";
import axios from "axios";
import store from "../../../store";
import { IApplicationService, IWebApplicationMessageCommunicatable } from "../../../domain/localhost";
import { Dictionary } from "../../../domain/system";
import { IClaioRegistry } from "../../../domain/claio";
import { IConfiguration } from "../../../domain/configuration";
import { IExternalImageRegistry } from "../../../domain/externalImage";
import {
  ExecuteResultTranslator,
  ExternalTemplateResultTranslator,
  ClaioSchemaParameterTranslator,
  ProRadDrawParameterTranslator,
  ApplicationLaunchRequestTranslator,
  WindowsOpenRequestTranslator,
  SignParameterTranslator,
} from "./translators";
import { User } from "../../../domain/user";
import { Patient } from "../../../domain/patient";
import { IExternalDataRequestResponseBean } from "./beans";
import { ExecuteRequestResponse, ExecuteRequest, ClaioSchemaExecuteRequest, BatchRequest, BatchResult, ExternalScriptExecuteResult, ProRadDrawExecuteRequest, ApplicationLaunchRequest, WindowsOpenRequest, SignExecuteRequest, ApplicationsLaunchRequest } from "../../../domain/localhost/items";
import { UrlSchemeClient } from "../../../utilities";
import { InternalScriptManager } from "./InternalScriptManager";
import { LoggerFactory } from "../../../utilities/logger";
import { IOcojoRegistry } from "../../../domain/ocojo";
import { IContentSource } from "../../../domain/drawing";
import { IExternalSystemRegistry } from "../../../domain/externalSystem";
import { ExternalWebApplicationManager } from "./ExternalWebApplicationManager";

export class ApplicationService implements IApplicationService {
  constructor(private _getToken: () => string, claio: IClaioRegistry, ocojo: IOcojoRegistry, externalImage: IExternalImageRegistry, externalSystem: IExternalSystemRegistry) {
    this._internalScriptManager = new InternalScriptManager(claio, ocojo, externalImage, externalSystem);
    this._externalWebApplicationManager = new ExternalWebApplicationManager();
  }
  private readonly _internalScriptManager: InternalScriptManager;
  private readonly _externalWebApplicationManager: ExternalWebApplicationManager;
  private readonly _logger = LoggerFactory.getLogger();

  private get token(): string {
    const token = this._getToken();
    if (!token) { throw new Error("token is empty!!!"); }
    return token;
  }

  public async launch(path: string, params: Dictionary<string, string>): Promise<void> {
    const config = store.getters.configuration as IConfiguration;
    const launchParameter = params ? params : new Dictionary<string, string>();
    launchParameter.set("token", this.token);
    if (!launchParameter.containsKey("action")) {
      // 基本的にセットされていないケースは無いはず
      launchParameter.set("action", "launch");
    }
    if (path) {
      launchParameter.set("path", path);
    }
    const query = launchParameter.keys.map(key => key + "=" + launchParameter.get(key)).join("&");
    const url = config.behavior.localhost.schemeUrlPrefix + "?" + query;
    const client = new UrlSchemeClient();
    client.request(url);
  }

  public async launchClaio(user: User, patient: Patient, departmentId: string, claioImageId: string, isMovie: boolean): Promise<void> {
    if (!user) { throw new Error("user is empty!!!"); }
    if (!patient) { throw new Error("patient is empty!!!"); }
    if (!departmentId) { throw new Error("departmentId is empty!!!"); }
    if (!claioImageId) { throw new Error("claioImageId is empty!!!"); }

    const param = new Dictionary<string, string>();
    param.set("action", "launch");
    param.set("target", "claio");
    param.set("uid", user.id);
    param.set("patientId", patient.id);
    param.set("departmentId", departmentId);
    param.set("claioImageId", claioImageId);
    param.set("isMovie", isMovie ? "true" : "false");
    await this.launch(null, param);
  }

  public async launchOcojo(user: User, patient: Patient, departmentId: string, imageId: string, isMovie: boolean): Promise<void> {
    // 現状Claio/Ocojo同時利用は要求されてないので、当面はClaioを起動する.同時利用が必要になったら要対応.
    // TODO: Assistant側対応後にOcojo起動用の実装を行う事.
    await this.launchClaio(user, patient, departmentId, imageId, isMovie);
  }

  public async launchResponsively(path: string, params: Dictionary<string, string>, additionalInfo: any): Promise<any> {
    if (this.isExecutableAsInternal(path, params, additionalInfo)) {
      this._logger.info("内部scriptを実行します.");
      return this._internalScriptManager.process(additionalInfo);
    }
    this._logger.info("外部scriptを実行します.");
    return this.launchResponsivelyAsExternal(path, params, additionalInfo);
  }

  private isExecutableAsInternal(path: string, params: Dictionary<string, string>, additionalInfo: any): boolean {
    const action = !!params ? params.get("action") : "";
    return (!path && action === "externalProcess" && (additionalInfo instanceof ExecuteRequest) && this.shouldProcess(additionalInfo, true));
  }

  private async launchResponsivelyAsExternal(path: string, params: Dictionary<string, string>, additionalInfo: any): Promise<any> {
    const response = await this.launchWithKey(path, params, additionalInfo);
    if (response.result) { return response.result; }
    const key = response.key;
    const result = await this.waitResult(key);
    return this.toExecuteResult(params.get("action"), result);
  }

  private toExecuteResult(action: string, bean: any): any {
    if (action === "mksTemplateQuery" || action === "dynamicTemplateQuery") {
      return ExternalTemplateResultTranslator.toExternalTemplatesInformation(bean);
    }
    if (action === "mksTemplate" || action === "dynamicTemplate") {
      return ExternalTemplateResultTranslator.toExternalTemplateExecuteResult(bean);
    }
    if (action === "mksTemplateCopy" || action === "dynamicTemplateCopy") {
      return ExternalTemplateResultTranslator.toExternalTemplateCopyResult(bean);
    }
    if (action === "claioSchema") {
      return ClaioSchemaParameterTranslator.toClaioSchemaExecuteResult(bean, this._getToken);
    }
    if (action === "cNoteSchema") {
      return ClaioSchemaParameterTranslator.toCNoteSchemaExecuteResult(bean, this._getToken);
    }
    if (action === "proRadDraw") {
      return ProRadDrawParameterTranslator.toProRadDrawExecuteResult(bean, this._getToken);
    }
    if (action === "sign") {
      return SignParameterTranslator.toSignExecuteResult(bean, this._getToken);
    }
    return ExecuteResultTranslator.toExternalScriptExecuteResult(bean);
  }

  private async waitResult(key: string): Promise<any> {
    const config = (store.getters.configuration as IConfiguration).behavior.localhost.externalScript.responseWaiting;

    // TODO:refactor
    const resultHolder = new Dictionary<string, any>();
    resultHolder.set(key, null);

    let watchCounter = 0;

    const resultGetFunction = () => {
      if (config.maxCount >= 0) {
        watchCounter++;
        if (watchCounter > config.maxCount) { 
          this._logger.warn("結果取得待ちでタイムアウトしました。");
          resultHolder.remove(key);
          return;
        }
      }
      if (this._externalWebApplicationManager.isWebApplicationClosed(key)) {
        // WEBアプリの画面が×ボタンで閉じられているときに分岐する
        // タイミングが前後すると正常に結果格納した場合でも閉じた判定が先行する可能性があるため、3秒待った後に結果が格納されていなければ閉じられたと判断している
        // 基本的にWEBアプリのバックグラウンド実行はしないはずなので、これで不足があるようであればそのときに手当てする
        setTimeout(() => {
          if (resultHolder.containsKey(key) && !resultHolder.get(key)) {
            this._logger.debug("WEBアプリケーションが結果格納前に閉じられています。");
            resultHolder.set(key, this.createEmptyResult());
          }
        }, 3000);
      }
      if (!resultHolder.containsKey(key)) {
        // 既にエラーになっている
        return;
      }
      if (resultHolder.get(key)) {
        // 既に結果を取得済み
        return;
      }
      this.getResultAsAny(key)
      .then(result => {
        if (result) {
          resultHolder.set(key, result);
        } else {
          setTimeout(resultGetFunction, config.intervalMilliSeconds);
        }
      })
      .catch(e => {
        this._logger.error(e);
        resultHolder.remove(key);
      });
    };
    setTimeout(resultGetFunction, 500);

    while (resultHolder.containsKey(key) && !resultHolder.get(key)) {
      await this.sleep(100);
    }
    return resultHolder.containsKey(key)
      ? resultHolder.get(key)
      : null;
  }

  private async sleep(msec: number) {
    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    await sleep(msec);
  }

  async launchReceiptPublish(receptionId: number) {
    if (!receptionId) { throw new Error("receptionId is empty!!!"); }

    const param = new Dictionary<string, string>();
    param.set("action", "receiptPublish");
    param.set("receptionId", receptionId.toString());
    this.launch(null, param);
  }

  async launchDetailReceiptPublish(receptionId: number, receptionDetailId: number) {
    if (!receptionId) { throw new Error("receptionId is empty!!!"); }
    if (!receptionDetailId) { throw new Error("detailId is empty!!!"); }

    const param = new Dictionary<string, string>();
    param.set("action", "receiptPublish");
    param.set("receptionId", receptionId.toString());
    param.set("receptionDetailId", receptionDetailId.toString());
    this.launch(null, param);
  }

  public async publishReceiptWithSpecifiedFormat(userCode: string, departmentCode: string, patientCode: string, receptionId: number, formatOriginalId: number, shouldSelectPrinter: boolean): Promise<void> {
    const param = new Dictionary<string, string>();
    param.set("action", "publishNewReceipt");
    param.set("userCode", userCode);
    param.set("departmentCode", departmentCode);
    param.set("patientNo", patientCode);
    param.set("receptionId", receptionId.toString());
    param.set("formatId", formatOriginalId.toString());
    param.set("shouldSelectPrinter", shouldSelectPrinter.toString());
    this.launch(null, param);
  }

  public async launchWithKey(path: string, params: Dictionary<string, string>, additionalInfo: any): Promise<ExecuteRequestResponse> {
    const request = this.toRequestBean(additionalInfo);
    const response = await this.getKey(request, params.get("action"));
    if (!response) { throw new Error("キー情報の登録に失敗しました。"); }
    if (!response.isRunningOnServer && !response.result) {
      if (this._externalWebApplicationManager.isProcessible(additionalInfo)) {
        this._externalWebApplicationManager.launch(response.key);
      } else {
        const launchParameter = params ? params : new Dictionary<string, string>();
        launchParameter.set("key", response.key);
        await this.launch(path, launchParameter);
      }
    }
    return this.toExecuteRequestResponse(response);
  }

  private toRequestBean(additionalInfo: any): any {
    if (!additionalInfo) { return null; }
    if (additionalInfo instanceof ExecuteRequest) {
      return ExecuteResultTranslator.toExternalDataRequestBean(additionalInfo);
    }
    if (additionalInfo instanceof ClaioSchemaExecuteRequest) {
      return ClaioSchemaParameterTranslator.toClaioSchemaExecuteRequestBean(additionalInfo);
    }
    if (additionalInfo instanceof ProRadDrawExecuteRequest) {
      return ProRadDrawParameterTranslator.toProRadDrawExecuteRequestBean(additionalInfo);
    }
    if (additionalInfo instanceof ApplicationLaunchRequest) {
      return ApplicationLaunchRequestTranslator.toApplicationLaunchRequestBean(additionalInfo);
    }
    if (additionalInfo instanceof ApplicationsLaunchRequest) {
      return ApplicationLaunchRequestTranslator.toApplicationsLaunchRequestBean(additionalInfo);
    }
    if (additionalInfo instanceof WindowsOpenRequest) {
      return WindowsOpenRequestTranslator.toWindowsOpenRequestBean(additionalInfo);
    }
    if (additionalInfo instanceof SignExecuteRequest) {
      return SignParameterTranslator.toSignExecuteRequestBean(additionalInfo);
    }
    return additionalInfo;
  }

  private toExecuteRequestResponse(bean: IExternalDataRequestResponseBean): ExecuteRequestResponse {
    if (!bean) { return null; }
    const response = new ExecuteRequestResponse();
    response.key = bean.key;
    response.result = ExecuteResultTranslator.toExternalScriptExecuteResult(bean.result);
    return response;
  }

  public async getResult(key: string, action: string): Promise<any> {
    const result = await this.getResultAsAny(key);
    if (!result && this._externalWebApplicationManager.isWebApplicationClosed(key)) {
      // WEBアプリの画面が×ボタンで閉じられているときに分岐する
      // タイミングが前後すると正常に結果格納した場合でも閉じた判定が先行するので再取得して確認している
      const reacquisitionResult = await this.getResultAsAny(key);
      if (!reacquisitionResult) {
        this._logger.debug("WEBアプリケーションが結果格納前に閉じられています。");
        return this.createEmptyResult();
      }
    }
    return this.toExecuteResult(action, result);
  }

  private createEmptyResult(): ExternalScriptExecuteResult {
    const emptyResult = new ExternalScriptExecuteResult();
    emptyResult.isComplete = true;
    emptyResult.isCancel = true;
    emptyResult.scriptLinkResults = [];
    return emptyResult;
  }

  private async getResultAsAny(key: string): Promise<any> {
    // 一回目の問い合わせでブラウザ内にキャッシュが作られてしまってapiに問い合わせに行かなくなってしまうので時刻を付与して暫定対応している
    const suppressor = Date.now().toString(16);
    const query = {
      params: {
        token: this.token,
        suppressor,
      },
    };
    const config = store.getters.configuration as IConfiguration;
    const url = config.behavior.localhost.httpUrlPrefix + "/api/local/result/" + key;
    this._logger.debug("[GET] " + url);
    const result = await axios.get(url, query);
    this._logger.debug(result.data);
    return result.data;
  }

  private async getKey(request: any, action: string): Promise<IExternalDataRequestResponseBean> {
    const query = {
      params: {
        action,
        token: this.token,
      },
    };
    const config = store.getters.configuration as IConfiguration;
    const url = config.behavior.localhost.httpUrlPrefix + "/api/local/register";
    this._logger.debug("[POST] " + url);
    this._logger.debug(request);
    const result = await axios.post(url, request, query);
    this._logger.debug(result.data);
    return result.data;
  }

  public async launchAsBatch(requests: BatchRequest[]): Promise<BatchResult[]> {
    return Enumerable.from(await this.launchInternalBatch(this.extractBatchRequests(requests, true)))
      .concat(await this.launchExternalBatch(this.extractBatchRequests(requests, false)))
      .concat(await this.launchWebApplicationBatch(this.extractBatchRequestsForWebApplication(requests)))
      .toArray();
  }

  private extractBatchRequestsForWebApplication(requests: BatchRequest[]): BatchRequest[] {
    return Enumerable.from(requests)
      .where(r => this._externalWebApplicationManager.isProcessible(r.request))
      .toArray();
  }

  private async launchWebApplicationBatch(requests: BatchRequest[]): Promise<BatchResult[]> {
    if (requests.length === 0) { return []; }

    // 基本的にWEBアプリのバックグラウンド実行はしないはずなので、これで不足があるようであればそのときに手当てする
    const batchResults: BatchResult[] = [];
    for (const request of requests) {
      const param = new Dictionary<string, string>();
      param.set("action", request.action);
      const result = await this.launchResponsivelyAsExternal(null, param, request.request);
      const batchResult = new BatchResult();
      batchResult.request = request;
      batchResult.result = result;
      batchResults.push(batchResult);
    }
    return batchResults;
  }

  private extractBatchRequests(requests: BatchRequest[], isInternal: boolean): BatchRequest[] {
    return Enumerable.from(requests)
      .where(r => !this._externalWebApplicationManager.isProcessible(r.request))
      .where(r => this.shouldProcess(r.request, isInternal))
      .toArray();
  }

  private shouldProcess(request: ExecuteRequest, isInternal: boolean): boolean {
    const configuration = store.state.configuration.behavior.localhost.internalScript;
    const isProcessibleInternal = configuration.isEnable && this._internalScriptManager.isProcessible(request);
    // TypeScript(JavaScript)にはbooleanのxorはない.
    return (isInternal && isProcessibleInternal)
        || (!isInternal && !isProcessibleInternal);
  }

  private async launchInternalBatch(requests: BatchRequest[]): Promise<BatchResult[]> {
    if (!requests || (requests.length <= 0)) {
      return [];
    }
    this._logger.info("内部scriptを" + requests.length + "件実行します.");
    const results = [] as BatchResult[];
    for (const request of requests) {
      results.push(await this.launchInternal(request));
    }
    return results;
  }

  private async launchInternal(request: BatchRequest): Promise<BatchResult> {
    const result = new BatchResult();
    result.request = request;
    result.result = await this._internalScriptManager.process(request.request);
    return result;
  }

  private async launchExternalBatch(requests: BatchRequest[]): Promise<BatchResult[]> {
    if (!requests || (requests.length <= 0)) {
      return [];
    }
    this._logger.info("外部scriptを" + requests.length + "件実行します.");
    const bean = ExecuteResultTranslator.toBatchRequestsBean(requests);
    const action = "batch";
    const response = await this.getKey(bean, action);
    if (!response) { throw new Error("キー情報の登録に失敗しました。"); }
    if (response.result) {
      throw new Error("TODO: implement");
    }
    if (!response.isRunningOnServer) {
      const param = new Dictionary<string, string>();
      param.set("action", action);
      param.set("key", response.key);
      await this.launch(null, param);
      await this.sleep(1000 + requests.length * 100);
    }
    const result = await this.waitResult(response.key);
    return ExecuteResultTranslator.toBatchResults(result, requests);
  }

  async executeAsInternalScript(path: string, params: Dictionary<string, string>, additionalInfo: any): Promise<ExternalScriptExecuteResult> {
    if (this.isExecutableAsInternal(path, params, additionalInfo)) {
      this._logger.info("内部scriptを実行します.");
      return this._internalScriptManager.process(additionalInfo);
    }
    return null;
  }

  async showPdfByClientViewer(source: IContentSource, fileName: string): Promise<void> {
    const param = new Dictionary<string, string>();
    param.set("action", "showPdf");
    param.set("source", encodeURIComponent(source.source));
    param.set("fileName", encodeURIComponent(fileName));
    await this.launch(null, param);
  }

  async registerLaunchRequest(additionalInfo: any): Promise<string> {
    const request = this.toRequestBean(additionalInfo);
    const response = await this.getKey(request, "registerLaunchRequest");
    return response.key;
  }

  async createWebApplicationMessageCommunicator(key: string): Promise<IWebApplicationMessageCommunicatable> {
    const request = await this.getExecuteRequest(key);
    return this._externalWebApplicationManager.createCommunicator(request, (r: ExternalScriptExecuteResult) => this.registerExecuteResult(key, r), (k: string) => this.getCommunicatorConfiguration(k), key);
  }

  private async getCommunicatorConfiguration(key: string): Promise<any> {
    if (!key) { throw new Error("key is empty!!!"); }

    const query = {
      params: {
        token: this.token,
      },
    };
    const config = store.getters.configuration as IConfiguration;
    const url = config.behavior.localhost.httpUrlPrefix + "/api/settings/communicator/" + key;
    this._logger.debug("[GET] " + url);
    const result = await axios.get(url, query);
    return result.data;
  }

  private async getExecuteRequest(key: string): Promise<ExecuteRequest> {
    if (!key) { throw new Error("key is empty!!!"); }

    const query = {
      params: {
        token: this.token,
      },
    };
    const config = store.getters.configuration as IConfiguration;
    const url = config.behavior.localhost.httpUrlPrefix + "/api/local/request/" + key;
    this._logger.debug("[GET] " + url);
    const result = await axios.get(url, query);
    return ExecuteResultTranslator.toExternalDataRequest(result.data);
  }

  private async registerExecuteResult(key: string, result: ExternalScriptExecuteResult): Promise<void> {
    if (!key) { throw new Error("key is empty!!!"); }
    if (!result) { throw new Error("result is empty!!!"); }

    const query = {
      params: {
        token: this.token,
      },
    };
    const config = store.getters.configuration as IConfiguration;
    const url = config.behavior.localhost.httpUrlPrefix + "/api/local/result/" + key;
    this._logger.debug("[POST] " + url);
    const data = ExecuteResultTranslator.toExternalScriptExecuteResultBean(result);
    await axios.post(url, data, query);
  }
}
